import { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import useExportModalView, { ExportModalView } from '@/hooks/useExportModalView';
import { ExportCustomFilters } from './ExportCustomFilters';
import { ExportFooter } from './ExportFooter';
import { ExportPeriod } from './ExportPeriod';
import { ExportScopeCards } from './ExportScopeCards';

interface ExportModalPanelProps {
  view: ExportModalView;
}

/** Header, scrolling body and sticky footer — shared by the dialog and the sheet. */
function ExportModalPanel({ view }: ExportModalPanelProps) {
  const { t } = useTranslation();
  const { state, busy, isPhone } = view;
  const Title = isPhone ? DrawerTitle : DialogTitle;
  const Description = isPhone ? DrawerDescription : DialogDescription;

  // Enter exports from anywhere but a button that has its own Enter.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const ownEnter = target.tagName === 'BUTTON' && target.getAttribute('role') !== 'radio';
    if (event.key !== 'Enter' || ownEnter || !state.canExport || busy) return;
    event.preventDefault();
    view.exportNow();
  };

  return (
    <div className='flex min-h-0 flex-1 flex-col' onKeyDown={onKeyDown}>
      <div className='flex items-start gap-3 px-5 pb-1 pt-5 sm:px-6 sm:pt-6'>
        <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sd-primary/25 text-sd-foreground'>
          <Download className='h-5 w-5' aria-hidden />
        </span>
        <div className='flex min-w-0 flex-1 flex-col gap-1 text-start'>
          <Title className='m-0 text-base font-semibold leading-6 text-sd-foreground'>
            {String(t('export_modal.title'))}
          </Title>
          <Description className='m-0 text-sm text-sd-muted-foreground'>
            {String(t('export_modal.subtitle'))}
          </Description>
        </div>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5 sm:px-6'>
        <ExportScopeCards state={state} disabled={busy} />
        {state.scope === 'custom' ? <ExportCustomFilters state={state} disabled={busy} /> : null}
        {state.showPeriod ? <ExportPeriod state={state} disabled={busy} /> : null}
      </div>

      <ExportFooter view={view} />

      {isPhone ? null : (
        <DialogClose
          aria-label={String(t('export_modal.close'))}
          className='absolute end-4 top-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-sd-muted-foreground transition-colors hover:bg-sd-muted hover:text-sd-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary'
        >
          <X className='h-4 w-4' aria-hidden />
        </DialogClose>
      )}
    </div>
  );
}

/**
 * BOXY-409 — the one orders export modal, opened from the header icon and the
 * bottom bar (useExportModalStore): a dialog on desktop, a bottom sheet on
 * phones. Pick a scope, see its live count, export.
 */
export const ExportModal = () => {
  const view = useExportModalView();
  const onOpenChange = (next: boolean) => (next ? null : view.dismiss());
  // Land on the chosen scope card, not the close button.
  const focusScope = (event: Event) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement | null)
      ?.querySelector<HTMLElement>('[role="radio"][data-state="checked"]')
      ?.focus();
  };

  if (view.isPhone) {
    return (
      <Drawer open={view.open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
        <DrawerContent
          onOpenAutoFocus={focusScope}
          className='sd-scope sd-font max-h-[92dvh] rounded-t-3xl [&>div:first-child]:bg-sd-border border-0 bg-white'
        >
          <ExportModalPanel view={view} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={view.open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={focusScope}
        // Owner 10-06: a stray click beside the modal threw the whole export
        // away. Only ✕, Cancel or Esc close it.
        onInteractOutside={(event) => event.preventDefault()}
        className='sd-scope sd-font top-[5vh] flex max-h-[90vh] translate-y-0 max-w-[560px] flex-col gap-0 overflow-hidden border-0 bg-white p-0 shadow-2xl sm:rounded-2xl [&>button:last-child]:hidden'
      >
        <ExportModalPanel view={view} />
      </DialogContent>
    </Dialog>
  );
};
